import { useEffect, useRef } from 'react';
import { getSupabase } from './supabaseClient';

/**
 * Subscribe a store to `postgres_changes` on a single Supabase table.
 *
 * IMPORTANT: we deliberately do NOT pass `filter: user_id=eq.<userId>`.
 * Supabase's default replica identity is the primary key only, so DELETE
 * replication events ship without the `user_id` column, a client-side
 * `user_id` filter therefore silently drops every DELETE, leaving phantom
 * rows on screen after another device deletes them. RLS already scopes
 * SELECT/INSERT/UPDATE/DELETE on these tables to `auth.uid() = user_id`,
 * so the server only sends us events for our own rows anyway.
 *
 * `handlers` is an object: { onInsert, onUpdate, onDelete }.
 * Each handler receives the raw row from the change event.
 *
 * Returns nothing, the hook tears the channel down on unmount or when
 * its inputs change. Safe no-op when `mode !== 'remote'` or no userId.
 *
 * Channel name includes a random per-instance suffix so two components
 * that both consume the same store hook (e.g. the global Quick-Add FAB
 * + the ContactsPage both calling useContactsStore) don't collide on a
 * shared Supabase channel.
 */
export const useTableRealtime = ({ table, userId, mode, onInsert, onUpdate, onDelete, enabled = true }) => {
  const instanceIdRef = useRef(null);
  if (instanceIdRef.current === null) {
    instanceIdRef.current = Math.random().toString(36).slice(2, 8);
  }

  // Handlers are stashed in a ref and updated on every render so the
  // realtime channel, which is subscribed once per (table, userId,
  // mode) tuple, always invokes the latest closures. Without this,
  // stores whose handlers depend on state that loads asynchronously
  // (e.g. useChecklistsStore.reverseLookup needs `itemIdByKey` which is
  // populated AFTER the channel subscribes) would receive every event
  // but execute against a stale empty lookup, silently dropping it.
  const handlersRef = useRef({ onInsert, onUpdate, onDelete });
  handlersRef.current = { onInsert, onUpdate, onDelete };

  useEffect(() => {
    if (!enabled) return undefined;
    if (mode !== 'remote' || !userId) return undefined;
    let cancelled = false;
    const supabase = getSupabase();
    const channel = supabase
      .channel(`bp-${table}-${userId}-${instanceIdRef.current}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table,
        },
        (payload) => {
          if (cancelled) return;
          const { onInsert: i, onUpdate: u, onDelete: d } = handlersRef.current;
          if (payload.eventType === 'INSERT' && i) i(payload.new);
          else if (payload.eventType === 'UPDATE' && u) u(payload.new, payload.old);
          else if (payload.eventType === 'DELETE' && d) d(payload.old);
        }
      )
      .subscribe((status) => {
        // Only surface failures, SUBSCRIBED is the happy path.
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          // eslint-disable-next-line no-console
          console.warn(`[realtime] ${table}: ${status}`);
        }
      });
    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table, userId, mode, enabled]);
};
